/* WDS plan picker — swaps the note line, re-renders sublines and the ATC
   button label when the plan or variant changes. Vanilla, no dependencies
   beyond Dawn's pubsub (used defensively if present).
   Dawn re-renders the buy-button markup after each variant change, which
   wipes the price label — a MutationObserver re-applies it (loop-safe:
   writes only when the text differs, never over sold-out states). */
(function () {
  class WdsPlanPicker extends HTMLElement {
    connectedCallback() {
      this.formId = this.dataset.formId;
      this.sectionId = this.dataset.sectionId;
      this.atcTemplate = this.dataset.atcTemplate || '';
      try {
        this.prices = JSON.parse(this.querySelector('[data-wds-plan-prices]').textContent);
      } catch (e) {
        this.prices = {};
      }
      this.noteEl = this.querySelector('[data-wds-plan-note]');
      this.radios = Array.from(this.querySelectorAll('input[name="selling_plan"]'));
      this.radios.forEach((r) => r.addEventListener('change', () => this.update()));

      // Dawn declares PUB_SUB_EVENTS as a script-scoped const (not a window
      // property), so test the bare binding, not window.PUB_SUB_EVENTS.
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (typeof subscribe === 'function' && events && events.variantChange) {
        this.unsubscribe = subscribe(events.variantChange, (event) => {
          const v = event && event.data && event.data.variant;
          if (v && v.id) this.variantId = String(v.id);
          this.update();
        });
      }

      // Dawn swaps buy-button DOM after variant changes; watch and re-apply.
      const scope = this.closest('product-info') || document.body;
      this.observer = new MutationObserver(() => {
        clearTimeout(this.reapplyTimer);
        this.reapplyTimer = setTimeout(() => this.update(), 60);
      });
      this.observer.observe(scope, { childList: true, subtree: true, characterData: true });

      this.variantId = this.currentVariantId();
      this.update();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.reapplyTimer);
    }

    currentVariantId() {
      const form = document.getElementById(this.formId);
      const idInput = form ? form.querySelector('input[name="id"]') : null;
      if (idInput && idInput.value) return String(idInput.value);
      // No product form on this template (e.g. buy buttons removed): Dawn
      // still writes ?variant= to the URL on every option change.
      try {
        const fromUrl = new URLSearchParams(window.location.search).get('variant');
        if (fromUrl && this.prices[fromUrl]) return String(fromUrl);
      } catch (e) {}
      if (this.variantId) return this.variantId;
      const keys = Object.keys(this.prices);
      return keys.length ? keys[0] : null;
    }

    priceFor(planId) {
      const vid = this.currentVariantId();
      const table = vid && this.prices[vid] ? this.prices[vid] : null;
      if (!table) return null;
      return table[planId || ''] || table[''] || null;
    }

    selectedRadio() {
      return this.radios.find((r) => r.checked) || this.radios[0];
    }

    update() {
      // Note line
      const sel = this.selectedRadio();
      if (sel && this.noteEl) {
        const note = sel.dataset.wdsNote || '';
        if (this.noteEl.textContent !== note) this.noteEl.textContent = note;
      }

      // Sublines ([price] token per row)
      this.querySelectorAll('[data-wds-subline]').forEach((el) => {
        const tpl = el.dataset.wdsTemplate || '';
        const price = this.priceFor(el.dataset.wdsPlanId);
        if (tpl && price) {
          const next = tpl.replace('[price]', price);
          if (el.textContent !== next) el.textContent = next;
        }
      });

      // ATC button label — never fight sold-out/unavailable states
      const price = sel ? this.priceFor(sel.value) : null;
      if (price && this.atcTemplate) {
        const btn = document.getElementById('ProductSubmitButton-' + this.sectionId);
        const span = btn ? btn.querySelector(':scope > span') : null;
        if (btn && span && !btn.disabled && !btn.hasAttribute('disabled')) {
          const next = this.atcTemplate.replace('[price]', price);
          if (span.textContent.trim() !== next) span.textContent = next;
        }
      }
      if (price) {
        const sticky = document.querySelector('[data-wds-sticky-price]');
        if (sticky && sticky.textContent !== price) sticky.textContent = price;
      }
    }
  }

  if (!customElements.get('wds-plan-picker')) {
    customElements.define('wds-plan-picker', WdsPlanPicker);
  }
})();
